"use client";
import { useState, useEffect } from "react";
import { ArrowRight, Leaf, Recycle, Users, Coins, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Poppins } from "next/font/google";
import Link from "next/link";
import {
  // getRecentReports,
  // getAllRewards,
  // getWasteCollectionTask,
  getImpactStats,
  // getCollectedWastes,
} from "@/utils/db/actions";
import AnalyticsDashboard from "@/components/AnalyticsDashboard";

const poppins = Poppins({
  weight: ["300", "400", "600"],
  subsets: ["latin"],
  display: "swap",
});

function AnimatedGlobe() {
  return (
    <div className="relative w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 mx-auto mb-6 sm:mb-8">
      <div className="absolute inset-0 rounded-full bg-green-500 opacity-20 animate-pulse"></div>
      <div className="absolute inset-2 rounded-full bg-green-400 opacity-40 animate-ping"></div>
      <div className="absolute inset-4 rounded-full bg-green-300 opacity-60 animate-spin"></div>
      <div className="absolute inset-6 rounded-full bg-green-200 opacity-80 animate-bounce"></div>
      <Leaf className="absolute inset-0 m-auto h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 text-green-600 animate-pulse" />
    </div>
  );
}

export default function Home() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [impactData, setImpactData] = useState({
    wasteCollected: 0,
    reportsSubmitted: 0,
    tokensEarned: 0,
    co2Offset: 0,
  });

  useEffect(() => {
    async function fetchImpactData() {
      try {
        // const reports = await getRecentReports(100);
        // const rewards = await getAllRewards();
        // const tasks = await getWasteCollectionTask(100);

        // const wasteCollected = tasks.reduce((total, task) => {
        //   const match = task.amount.match(/(\d+(\.\d+)?)/);
        //   const amount = match ? parseFloat(match[0]) : 0;
        //   return total + amount;
        // }, 0);

        // const reportsSubmitted = reports.length;
        // const tokensEarned = rewards.reduce(
        //   (total, reward) => total + (reward.points || 0),
        //   0
        // );
        // const co2Offset = wasteCollected * 0.5;

        // setImpactData({
        //   wasteCollected: Math.round(wasteCollected * 10) / 10,
        //   reportsSubmitted,
        //   tokensEarned,
        //   co2Offset: Math.round(co2Offset * 10) / 10,
        // });
        const stats = await getImpactStats();
        setImpactData({
          wasteCollected: Math.round(stats.wasteCollected * 10) / 10,
          reportsSubmitted: stats.reportsSubmitted,
          tokensEarned: stats.tokensEarned,
          co2Offset: Math.round(stats.co2Offset * 10) / 10,
        });
      } catch (error) {
        console.error("Error fetching impact data:", error);
        setImpactData({
          wasteCollected: 0,
          reportsSubmitted: 0,
          tokensEarned: 0,
          co2Offset: 0,
        });
      }
    }

    fetchImpactData();
  }, []);

  const login = () => setLoggedIn(true);

  return (
    <div
      className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 ${poppins.className}`}
    >
      {/* Hero Section */}
      <section className="text-center mb-16 sm:mb-20">
        <AnimatedGlobe />
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-gray-800 tracking-tight">
          Smart <span className="text-green-600">Waste Management</span>
        </h1>
        <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed mb-8">
          Join our community in making waste management more efficient and
          rewarding!
        </p>
        {!loggedIn ? (
          <Button
            onClick={login}
            className="bg-green-600 hover:bg-green-700 text-white text-base sm:text-lg px-6 sm:px-8 md:px-10 py-4 sm:py-5 md:py-6 rounded-full font-medium transition duration-300 transform hover:scale-105"
          >
            Get Started
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        ) : (
          <Link href="/report">
            <Button className="bg-green-600 hover:bg-green-700 text-white text-base sm:text-lg px-6 sm:px-8 md:px-10 py-4 sm:py-5 md:py-6 rounded-full font-medium transition duration-300 transform hover:scale-105">
              Report Waste
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        )}
      </section>

      {/* Features Section */}
      <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 md:gap-10 mb-16 sm:mb-20">
        <FeatureCard
          icon={Leaf}
          title="Eco-Friendly"
          description="Contribute to a cleaner environment by reporting and collecting waste."
        />
        <FeatureCard
          icon={Coins}
          title="Earn Rewards"
          description="Get tokens for your contributions to waste management efforts."
        />
        <FeatureCard
          icon={Users}
          title="Community-Driven"
          description="Be part of a growing community committed to sustainable practices."
        />
      </section>

      {/* Impact Section */}
      <section className="bg-white p-6 sm:p-10 rounded-2xl sm:rounded-3xl shadow-lg mb-16">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-10 text-center text-gray-800">
          Our Impact
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
          <ImpactCard
            title="Waste Collected"
            value={`${impactData.wasteCollected} kg`}
            icon={Recycle}
          />
          <ImpactCard
            title="Reports Submitted"
            value={impactData.reportsSubmitted.toString()}
            icon={MapPin}
          />
          <ImpactCard
            title="Tokens Earned"
            value={impactData.tokensEarned.toString()}
            icon={Coins}
          />
          <ImpactCard
            title="CO2 Offset"
            value={`${impactData.co2Offset} kg`}
            icon={Leaf}
          />
        </div>
        <AnalyticsDashboard />
      </section>
    </div>
  );
}

function ImpactCard({
  title,
  value,
  icon: Icon,
}: {
  title: string;
  value: string | number;
  icon: React.ElementType;
}) {
  const formattedValue =
    typeof value === "number"
      ? value.toLocaleString("en-US", { maximumFractionDigits: 1 })
      : value;

  return (
    <div className="p-5 sm:p-6 rounded-xl bg-gray-50 border border-gray-100 transition-all duration-300 hover:shadow-md text-center">
      <Icon className="h-8 w-8 sm:h-10 sm:w-10 text-green-500 mb-4 mx-auto" />
      <p className="text-2xl sm:text-3xl font-bold mb-2 text-gray-800">
        {formattedValue}
      </p>
      <p className="text-sm sm:text-base text-gray-600">{title}</p>
    </div>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
}) {
  return (
    <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex flex-col items-center text-center">
      <div className="bg-green-100 p-3 sm:p-4 rounded-full mb-4 sm:mb-6">
        <Icon className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
      </div>
      <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-4 text-gray-800">
        {title}
      </h3>
      <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
        {description}
      </p>
    </div>
  );
}
